import { conversationService } from 'config/api';
import { messageTypes, CLIENT_CREATION_MESSAGE_SUBJECTS } from 'constants';
import { isValidEmail, phoneNumberFormatter, uuidv4, isHTML } from 'utils';

const updateContact = (contact) => (!isValidEmail(contact) ? phoneNumberFormatter(contact, 'plus1') : contact);

export const sendMessage = async ({
  recipients,
  subject,
  message,
  messageType,
  associateId,
  associateContact,
  imageSource,
  attachments,
  postMutation,
  sendAttachedFile,
  tags = {},
  templateAttributes = {},
  emailTemplate = '',
  stopOnBrokenAttachments,
  preferredName,
}) => {
  const direction = 'Outbound';
  const campaignId = recipients.length > 1 ? uuidv4() : null;
  const trimmedMsg = message.trim();
  // uploading all images to S3 bucket before sending message
  const imageUploadResult = await Promise.all(
    imageSource
      .filter((source) => !source?.item?.isSharedLook)
      .map((source) => {
        const file = attachments.find(({ item: el, id }) => !el.isSharedProduct && id === source.id);
        return sendAttachedFile(file?.item || file, source);
      }),
  );

  const failedImages = imageUploadResult.filter(({ resolution }) => resolution === 'error');

  const getMessageBody = () => {
    return emailTemplate.trim() || trimmedMsg;
  };

  const urls = imageSource.reduce(
    (acc, source) => {
      const { actualImageUrl, thumbnailUrl } = source;
      if (actualImageUrl) {
        acc.actualImageUrls.push(actualImageUrl);
        acc.thumbnailUrls.push(thumbnailUrl || actualImageUrl);
      }
      return acc;
    },
    { actualImageUrls: [], thumbnailUrls: [] },
  );

  try {
    const parameters = {
      InternalEntityId: associateId,
      InternalAddress: associateContact,
      ExternalEntities: recipients.map((r) => ({
        ExternalEntityId: updateContact(r.contact),
        ExternalAddress: updateContact(r.contact),
        ExternalEntityName: r.fullName,
      })),
      Direction: direction,
      MessageType: messageType,
      SourceId: 'OSP',
      MediaUrls: urls.actualImageUrls,
      ThumbnailUrls: urls.thumbnailUrls,
      Attachments: urls.actualImageUrls,
      CampaignId: campaignId,
      Tags: tags,
      Subject: subject,
      TemplateAttributes: templateAttributes,
      Body: getMessageBody(),
      PreferredName: preferredName,
    };

    const { data } = await conversationService.post('/messages', parameters);
    const messageId = data.sid;
    const dateCreated = new Date(data.timestamp);

    let messageBody = getMessageBody();
    messageBody = isHTML(messageBody) ? messageBody.replace(/(?:\r\n|\r|\n)/g, '<br>') : messageBody;
    const message = JSON.stringify({ body: messageBody, mediaUrls: urls.actualImageUrls });
    const recipientsContact = recipients.map((r) => updateContact(r.contact));

    postMutation({
      variables: {
        message,
        messageId,
        associateId,
        contactId: recipientsContact,
        message_type: messageType,
        date_created: dateCreated,
        message_direction: direction,
        message_subject: subject,
      },
    });

    if (failedImages.length && stopOnBrokenAttachments) {
      return {
        error: 'Failed to upload images.',
        data: failedImages.map(({ data }) => data),
      };
    }
  } catch (err) {
    return null;
  }
};

export const sendMessageWithAttributes = ({ user, config, customer, templateType, postMutation }) => {
  const commsHubConfig = config.get('commsHub');
  const { firstName, lastName, email } = customer;
  const { groupId, templates } = commsHubConfig[user.brand];
  const subjectText = CLIENT_CREATION_MESSAGE_SUBJECTS[user.brand][templateType];
  const templateAttributes = {
    SenderName: `${user.firstName} ${user.lastName}`,
    GroupId: groupId,
    TemplateId: templates[templateType],
  };

  if (templateType === 'cmdLinking') {
    templateAttributes.CMD = {
      customer: {
        first_name: firstName,
        last_name: lastName,
      },
    };
  }

  sendMessage({
    recipients: [
      {
        fullName: `${firstName} ${lastName}`,
        contact: email,
      },
    ],
    subject: subjectText,
    message: subjectText,
    messageType: messageTypes.email,
    associateId: user.employeeNumber,
    associateContact: user.email,
    imageSource: [],
    attachments: [],
    postMutation,
    templateAttributes,
  });
};
